import * as React from "react"

import { getStorageItem, setStorageItem } from "@exxatdesignux/ui/lib/persisted-state"

import type { Assessment } from "@/lib/mock/exam-data"

export const CREATED_ASSESSMENTS_STORE_KEY = "exxat.exam.created-assessments:v1"

const listeners = new Set<() => void>()
let version = 0

function emit() {
  version += 1
  for (const listener of listeners) listener()
}

function readStore(raw: string | null): Assessment[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((row): row is Assessment => {
      return Boolean(
        row &&
          typeof row === "object" &&
          typeof (row as Assessment).id === "string" &&
          typeof (row as Assessment).title === "string",
      )
    })
  } catch {
    return []
  }
}

export function readCreatedAssessments(): Assessment[] {
  return readStore(getStorageItem(CREATED_ASSESSMENTS_STORE_KEY))
}

export function rememberCreatedAssessment(row: Assessment) {
  const next = readCreatedAssessments().filter((item) => item.id !== row.id)
  next.push(row)
  setStorageItem(CREATED_ASSESSMENTS_STORE_KEY, JSON.stringify(next))
  emit()
}

export function forgetCreatedAssessment(assessmentId: string) {
  const next = readCreatedAssessments().filter(
    (item) => item.id !== assessmentId && item.code !== assessmentId,
  )
  setStorageItem(CREATED_ASSESSMENTS_STORE_KEY, JSON.stringify(next))
  emit()
}

/** Merge persisted creates into the live roster so the hub list stays current. */
export function hydrateCreatedAssessments(target: Assessment[]) {
  for (const row of readCreatedAssessments()) {
    if (!target.some((item) => item.id === row.id)) {
      target.push(row)
    }
  }
  // A status moved in this browser outlives a reload, for seeded rows too.
  const overrides = readStatusOverrides()
  for (const row of target) {
    const status = overrides[row.id]
    if (status && row.status !== status) row.status = status
  }
}

const STATUS_OVERRIDES_KEY = "exxat.exam.assessment-status:v1"

function readStatusOverrides(): Record<string, Assessment["status"]> {
  try {
    const raw = getStorageItem(STATUS_OVERRIDES_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : {}
    return parsed && typeof parsed === "object" ? (parsed as Record<string, Assessment["status"]>) : {}
  } catch {
    return {}
  }
}

export function rememberAssessmentStatus(assessmentId: string, status: Assessment["status"]) {
  setStorageItem(
    STATUS_OVERRIDES_KEY,
    JSON.stringify({ ...readStatusOverrides(), [assessmentId]: status }),
  )
  emit()
}

/** Re-render when any assessment is created, deleted or changes status. */
export function useCreatedAssessmentsVersion(): number {
  return React.useSyncExternalStore(
    subscribeCreatedAssessments,
    createdAssessmentsVersion,
    () => 0,
  )
}

export function subscribeCreatedAssessments(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function createdAssessmentsVersion() {
  return version
}
